import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { OrganizationPermissions, organizationApi } from '../utils/organizationApi';

interface PermissionsContextType {
  permissions: OrganizationPermissions | null;
  loading: boolean;
  refreshPermissions: () => Promise<void>;
  canViewMenu: (menuKey: keyof OrganizationPermissions['menuPermissions']) => boolean;
  canShowAlert: (alertKey: keyof OrganizationPermissions['alertPermissions']) => boolean;
  canCreateAgents: boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentOrganization, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<OrganizationPermissions | null>(null);
  const [loading, setLoading] = useState(false);

  const loadPermissions = async () => {
    console.log('PermissionsContext - loadPermissions called', { currentOrganization, isAuthenticated });
    
    if (!currentOrganization || !isAuthenticated) {
      console.log('PermissionsContext - No organization or not authenticated, setting permissions to null');
      setPermissions(null);
      return;
    }

    setLoading(true);
    try {
      console.log('PermissionsContext - Loading permissions for organization:', currentOrganization.id);
      const organization = await organizationApi.get(currentOrganization.id);
      console.log('PermissionsContext - Loaded organization permissions:', organization.permissions);
      setPermissions(organization.permissions);
    } catch (error) {
      console.error('Failed to load organization permissions:', error);
      // Fallback para permissões padrão em caso de erro
      const defaultPermissions = organizationApi.getDefaultPermissions();
      console.log('PermissionsContext - Using default permissions:', defaultPermissions);
      setPermissions(defaultPermissions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [currentOrganization, isAuthenticated]);

  const canViewMenu = (menuKey: keyof OrganizationPermissions['menuPermissions']): boolean => {
    // Se não há permissões carregadas, usar permissões padrão (mais permissivas para não quebrar a aplicação)
    const result = permissions ? permissions.menuPermissions[menuKey] : true;
    console.log(`PermissionsContext - canViewMenu(${menuKey}):`, result, { 
      permissions: permissions?.menuPermissions,
      hasPermissions: !!permissions 
    });
    return result;
  };

  const canShowAlert = (alertKey: keyof OrganizationPermissions['alertPermissions']): boolean => {
    // Se não há permissões carregadas, usar permissões padrão (mais permissivas para não quebrar a aplicação)
    const result = permissions ? permissions.alertPermissions[alertKey] : true;
    console.log(`PermissionsContext - canShowAlert(${alertKey}):`, result, { 
      permissions: permissions?.alertPermissions,
      hasPermissions: !!permissions 
    });
    return result;
  };

  const canCreateAgents = permissions?.canCreateAgents ?? true;

  const value: PermissionsContextType = {
    permissions,
    loading,
    refreshPermissions: loadPermissions,
    canViewMenu,
    canShowAlert,
    canCreateAgents,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
